import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import type { AgentPresence } from "../utils/agent-presence-utils"
import type { HostedAgentSession, HostedAgentSummary } from "@/api/client/hosted-agents"
import type { HostedAgentArtifact } from "@/api/client/hosted-agents-artifacts"
import type { ChatSessionModel } from "@/api/models"
import type { SessionState } from "@/stores/slices/chat-slice"

import { markAgentRunReadApi } from "@/api/client/messages"
import { ArrowLeft, History, Plus, SearchX } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { useRoutes } from "@/contexts/use-routes"
import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useSessionId } from "@/hooks/use-session-id"
import { queryKeys } from "@/lib/query-keys"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"
import { useHostedAgentArtifactSeedStore } from "@/stores/hosted-agent-artifact-seed-store"

import { AgentArtifactsSection } from "./agent-artifacts-section"
import { AgentAudience } from "./agent-audience"
import { AgentAvatar } from "./agent-avatar"
import { AgentChatView } from "./agent-chat-view"
import { AgentDeliveryReceipts } from "./agent-delivery-receipts"
import { AgentM365Pill } from "./agent-m365-pill"
import { AgentThreadList } from "./agent-thread-list"
import { AgentWorkingStateSection } from "./agent-working-state-section"
import { ArtifactViewerDialog } from "./artifact-viewer-dialog"
import { useHostedAgentArtifact } from "../hooks/use-hosted-agent-artifacts"
import {
  useHostedAgent,
  useHostedAgents,
  useHostedAgentSessions,
  useResolveHostedAgentForSession,
} from "../hooks/use-hosted-agents"
import {
  buildContinueFromArtifactText,
  libraryArtifactDisplayContent,
  primeContinuedArtifactBase,
} from "../utils/agent-artifacts-utils"
import { agentThreadTitle } from "../utils/agents-utils"

/**
 * Hosted-agent detail view (HERMES Fas 4): agent header + thread rail (B6:
 * grouped on each session's STAMPED visibility) + the chat view, which
 * reuses the chat-turns stack.
 *
 * Mounted for both `/agents/:agentSlug` (fresh thread) and
 * `/agents/chat/:sessionId` (opened thread) — the same route element, so an
 * in-flight stream survives the create-on-send navigation between them. On
 * the chat path the agent is resolved from the session's stamped
 * `hostedAgentId` (own sessions), the cached per-agent thread lists (foreign
 * open threads reached from the rail), or — on a cold load where both are
 * empty — by matching the session against each visible agent's thread list.
 */
export function AgentDetailModule() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { getRoutePath, getRouteChatPath } = useRoutes()
  const sessionType = useCurrentSessionType()
  const { agentSlug } = useParams<{ agentSlug?: string }>()
  const currentSessionId = useSessionId()

  const rosterQuery = useHostedAgents()
  const slugAgentQuery = useHostedAgent(agentSlug)
  // Own sessions for this module (same cache useSessionSync reads) — used to
  // resolve the agent behind /agents/chat/:sessionId via the stamped
  // hostedAgentId, and as the pin target for foreign open threads.
  const ownSessionsQuery = useSessionsQuery(sessionType)

  const resolvedAgent: HostedAgentSummary | undefined = useMemo(() => {
    if (agentSlug) return slugAgentQuery.data
    if (!currentSessionId) return undefined
    const agents = rosterQuery.data ?? []
    const ownSession = ownSessionsQuery.data?.find(
      (session) => session != null && session.id === currentSessionId
    ) as HostedAgentSession | undefined
    if (ownSession?.hostedAgentId) {
      return agents.find((candidate) => candidate.id === ownSession.hostedAgentId)
    }
    // Foreign open thread: it is not in the own-session list, but it IS in
    // the per-agent thread list the user clicked it in.
    for (const candidate of agents) {
      const cached = queryClient.getQueryData<HostedAgentSession[]>(
        queryKeys.hostedAgentSessions(candidate.slug)
      )
      if (cached?.some((session) => session.id === currentSessionId)) return candidate
    }
    return undefined
  }, [
    agentSlug,
    slugAgentQuery.data,
    currentSessionId,
    rosterQuery.data,
    ownSessionsQuery.data,
    queryClient,
  ])

  // Cold load of a foreign OPEN thread (hard refresh / fresh tab): it is in no
  // cache and not in `/api/list-sessions`, so `resolvedAgent` above misses it.
  // Once the roster and own-session list have both settled without a match, ask
  // each visible agent's thread list which one owns the session (B6: open
  // threads are returned to their readers). Gated on `agentSlug`-less session
  // paths only — the slug path resolves directly from the roster.
  const needsColdResolve =
    !agentSlug &&
    !!currentSessionId &&
    !resolvedAgent &&
    rosterQuery.isSuccess &&
    ownSessionsQuery.isSuccess
  const coldResolveQuery = useResolveHostedAgentForSession(
    currentSessionId,
    rosterQuery.data,
    needsColdResolve
  )

  // On the session path, resolution briefly gaps during the create-on-send
  // navigation (the optimistic session row and the per-agent rail cache can
  // both lag one fetch). Falling back to the last resolved agent keeps the
  // module — and the in-flight stream — mounted through that gap. The slug
  // path never falls back: uniform 404 must stay visible there.
  const resolved = resolvedAgent ?? coldResolveQuery.data
  const lastAgentRef = useRef<HostedAgentSummary | undefined>(undefined)
  useEffect(() => {
    if (resolved) lastAgentRef.current = resolved
  }, [resolved])
  // Keep a live create-on-send transition mounted only while resolution is
  // unfinished. A definitive miss must not leave a different, previously
  // visited agent impersonating the unknown/revoked session forever.
  const agent =
    resolved ?? (agentSlug || coldResolveQuery.isSuccess ? undefined : lastAgentRef.current)

  const sessionsQuery = useHostedAgentSessions(agent?.slug)
  const currentSession = sessionsQuery.data?.find((session) => session.id === currentSessionId)

  // Header subtitle: the thread's own title. The backend titles a thread
  // shortly after the first turn; until then the first user message stands in
  // (the chat store is the only place that holds it), so the header never
  // reads "Untitled thread" on a thread the user just started.
  const explorerStore = useAIExplorerStore(sessionType)
  const firstUserMessage = explorerStore((state) => {
    // The index signature is not `noUncheckedIndexedAccess`-guarded, so the
    // miss has to be typed explicitly: a session with no store row yet is
    // normal (cold load, foreign open thread).
    const sessionState = currentSessionId
      ? (state.sessionStates[currentSessionId] as SessionState | undefined)
      : undefined
    return sessionState?.messages[0]?.[0]
  })

  const basePath = getRoutePath("agents")
  const chatPath = getRouteChatPath("agents")
  const goBack = () => void navigate(basePath)

  /**
   * `useSessionSync` (inside the chat view's `useChatHistory`) can only open
   * sessions present in the module's own session-list cache. Foreign open
   * threads never appear in `/api/list-sessions`, so upsert them into that
   * cache before (and while) they are open — otherwise the sync redirects
   * back to the roster after its grace period.
   */
  const pinSessionForSync = useCallback(
    (session: HostedAgentSession) => {
      if (!session.id) return
      queryClient.setQueryData(
        queryKeys.sessionHistory(sessionType, undefined),
        (oldSessions: ChatSessionModel[] | undefined) => {
          const sessions = oldSessions ?? []
          if (sessions.some((existing) => existing?.id === session.id)) return sessions
          return [session, ...sessions]
        }
      )
    },
    [queryClient, sessionType]
  )

  useEffect(() => {
    if (!currentSessionId || !agent) return
    if (ownSessionsQuery.data?.some((session) => session?.id === currentSessionId)) return
    const cached = queryClient.getQueryData<HostedAgentSession[]>(
      queryKeys.hostedAgentSessions(agent.slug)
    )
    const target = cached?.find((session) => session.id === currentSessionId)
    if (target) pinSessionForSync(target)
  }, [agent, currentSessionId, ownSessionsQuery.data, pinSessionForSync, queryClient])

  // Mobile-only thread drawer (DESIGN.md: never hide desktop content on
  // mobile — below `sm` the rail moves into a sheet instead of disappearing).
  const [isThreadSheetOpen, setThreadSheetOpen] = useState(false)
  const [activePresence, setActivePresence] = useState<AgentPresence | undefined>(undefined)

  const setPendingSeed = useHostedAgentArtifactSeedStore((state) => state.setPendingSeed)
  const setPendingComposerText = useHostedAgentArtifactSeedStore(
    (state) => state.setPendingComposerText
  )

  // Opening a thread the agent has answered in (run done, unread) is the
  // "I have looked" signal: clear unread so the list stops showing the thread
  // as waiting for the user, then refetch so the row updates.
  useEffect(() => {
    if (!currentSessionId || !agent?.slug) return
    const session = sessionsQuery.data?.find((candidate) => candidate.id === currentSessionId)
    const run = session?.agent_run
    if (!run?.unread || run.status === "executing" || run.status === "synthesizing") return
    void markAgentRunReadApi(currentSessionId)
      .then(() =>
        queryClient.invalidateQueries({ queryKey: queryKeys.hostedAgentSessions(agent.slug) })
      )
      .catch(() => undefined)
  }, [currentSessionId, agent?.slug, sessionsQuery.data, queryClient])

  // When the open thread's run settles, the list's stamped state for it is
  // stale (working → done/failed): refetch so the rail agrees with the chat.
  const previousPresence = useRef(activePresence)
  useEffect(() => {
    const before = previousPresence.current
    previousPresence.current = activePresence
    const settled =
      activePresence === "done" || activePresence === "blocked" || activePresence === "stopped"
    const wasBusy = before === "working" || before === "thinking" || before === "waiting"
    if (settled && wasBusy && agent?.slug)
      void queryClient.invalidateQueries({ queryKey: queryKeys.hostedAgentSessions(agent.slug) })
  }, [activePresence, agent?.slug, queryClient])

  const handleSelectThread = (session: HostedAgentSession) => {
    setThreadSheetOpen(false)
    // Opening an existing thread abandons any "continue working" seed that
    // was waiting for a NEW thread.
    setPendingSeed(null)
    if (!session.id || session.id === currentSessionId) return
    pinSessionForSync(session)
    void navigate(`${chatPath}/${session.id}`)
  }

  // An attachment uploaded from the greeting state forces the thread into
  // existence before the turn (#3021). `ensureSession` only announces it
  // through `history.replaceState`, which React Router cannot see — so this
  // view would still read a slug-only URL and its session sync would close
  // the thread the files were just indexed against, leaving the turn to
  // create a second one. Navigating for real keeps both on one session.
  const pendingAdoptionRef = useRef<{
    sessionId: string
    locationKey: string
    resolve: (adopted: boolean) => void
  } | null>(null)
  useEffect(() => {
    const pending = pendingAdoptionRef.current
    if (!pending) return
    if (currentSessionId === pending.sessionId || location.key !== pending.locationKey) {
      pendingAdoptionRef.current = null
      pending.resolve(currentSessionId === pending.sessionId)
    }
  }, [currentSessionId, location.key])
  useEffect(
    () => () => {
      pendingAdoptionRef.current?.resolve(false)
      pendingAdoptionRef.current = null
    },
    []
  )
  const handleAdoptEnsuredSession = useCallback(
    (sessionId: string): Promise<boolean> => {
      if (!agent) return Promise.resolve(false)
      if (sessionId === currentSessionId) return Promise.resolve(true)
      pendingAdoptionRef.current?.resolve(false)
      return new Promise((resolve) => {
        pendingAdoptionRef.current = { sessionId, locationKey: location.key, resolve }
        void navigate(`${chatPath}/${sessionId}`, { replace: true })
      })
    },
    [agent, chatPath, currentSessionId, location.key, navigate]
  )

  // New thread: navigating to the slug path clears the open session via
  // useSessionSync (no URL session id → closeSession + currentSessionId
  // null), which resets the chat surface to the greeting state. The binding
  // for the NEXT created session stays registered by useAgentChat.
  const handleNewThread = () => {
    if (!agent) return
    setPendingSeed(null)
    void navigate(`${basePath}/${agent.slug}`)
  }

  // B4: artifact library. The viewer dialog is owned here (the rail section
  // renders twice — desktop aside + mobile sheet — but there is one viewer).
  const [viewedArtifact, setViewedArtifact] = useState<HostedAgentArtifact | null>(null)
  const [isArtifactViewerOpen, setArtifactViewerOpen] = useState(false)

  const linkedArtifactId = new URLSearchParams(location.search).get("artifact") ?? undefined
  const linkedArtifact = useHostedAgentArtifact(agent?.slug, linkedArtifactId)
  useEffect(() => {
    if (
      linkedArtifact.data &&
      linkedArtifactId &&
      linkedArtifact.data.artifact.id === linkedArtifactId
    ) {
      setViewedArtifact(linkedArtifact.data.artifact)
      setArtifactViewerOpen(true)
      const search = new URLSearchParams(location.search)
      search.delete("artifact")
      void navigate({ pathname: location.pathname, search: search.toString() }, { replace: true })
    }
  }, [linkedArtifact.data, linkedArtifactId, location.pathname, location.search, navigate])
  useEffect(() => {
    if (linkedArtifact.isError) toast.error(t("agents.artifacts.error"))
  }, [linkedArtifact.isError, t])

  const handleOpenArtifact = (artifact: HostedAgentArtifact) => {
    setThreadSheetOpen(false)
    setViewedArtifact(artifact)
    setArtifactViewerOpen(true)
  }

  /** "Reuse": drop the artifact's content into the composer as context, wherever the user is. */
  const handleReuseArtifact = (artifact: HostedAgentArtifact) => {
    if (!artifact.content) return
    setPendingComposerText(libraryArtifactDisplayContent(artifact.content))
    setArtifactViewerOpen(false)
    toast.success(t("agents.artifacts.reuse_done"))
  }

  /**
   * "Continue working": a fresh thread whose first message starts from the
   * artifact, and a seed so publishing from that thread proposes vN+1.
   * Navigate first, then arm the seed — the chat view binds a seed only on a
   * null → session-id transition, so the thread being left can't claim it.
   */
  const handleContinueArtifact = (artifact: HostedAgentArtifact) => {
    if (!agent || !artifact.content) return
    setArtifactViewerOpen(false)
    void navigate(`${basePath}/${agent.slug}`)
    setPendingSeed({
      agentSlug: agent.slug,
      artifactId: artifact.id,
      rootId: artifact.root_id,
      title: artifact.title,
      version: artifact.version,
    })
    // The continued artifact is a real base, so an update against it applies (#3207).
    primeContinuedArtifactBase(artifact)
    setPendingComposerText(
      buildContinueFromArtifactText(
        t("agents.artifacts.continue_intro", {
          title: artifact.title,
          version: artifact.version,
        }),
        artifact
      )
    )
  }

  const isResolvingAgent =
    rosterQuery.isPending ||
    (!agentSlug &&
      !!currentSessionId &&
      !agent &&
      (ownSessionsQuery.isPending || (needsColdResolve && coldResolveQuery.isPending)))

  if (isResolvingAgent) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <div className="skeleton-warm h-12 w-72 rounded-lg" />
        <div className="skeleton-warm h-64 rounded-lg" />
      </div>
    )
  }

  // Unknown slug and access-denied are indistinguishable by design (uniform
  // 404). A session URL whose agent can't be resolved (e.g. a foreign thread
  // opened after a hard refresh, or a revoked agent) lands here too.
  if (!agent) {
    return (
      <EmptyState
        className="h-full"
        description={t("agents.detail.not_found_description")}
        icon={SearchX}
        title={t("agents.detail.not_found_title")}
        primaryAction={
          <Button onClick={goBack}>
            <ArrowLeft aria-hidden="true" className="size-4" />
            {t("agents.detail.back")}
          </Button>
        }
      />
    )
  }

  const threadLabel = currentSessionId
    ? agentThreadTitle(currentSession, firstUserMessage) || t("agents.detail.untitled_thread")
    : t("agents.detail.new_thread")
  const headerSubtitle = [agent.profile.title.trim(), threadLabel].filter(Boolean).join(" \u00b7 ")

  return (
    <div className="flex h-full min-h-0 flex-col [--radius:0.75rem]">
      <header className="bg-card flex h-16 shrink-0 items-center gap-3 border-b pr-4 pl-3">
        <Button
          aria-label={t("agents.detail.back")}
          className="shrink-0"
          size="icon"
          variant="ghost"
          onClick={goBack}
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
        </Button>
        <AgentAvatar agent={agent} className="size-10 shrink-0" />
        <div className="min-w-0 flex-1">
          <h4 className="font-display truncate text-[17px] leading-tight">{agent.profile.name}</h4>
          <p className="text-muted-foreground truncate text-xs" data-testid="agent-header-subtitle">
            {headerSubtitle}
          </p>
        </div>
        {/* Audience and connection are thread state, so they live with the
            thread's identity in the header — not as loose controls elsewhere. */}
        <AgentAudience
          agent={agent}
          className="max-w-28 shrink-0 sm:max-w-52"
          session={currentSession}
          sessionId={currentSessionId ?? null}
        />
        <AgentM365Pill className="hidden shrink-0 sm:inline-flex" />
        {/* Below `sm` the thread rail lives in a sheet; this button is its only entry point. */}
        <Button
          aria-label={t("agents.detail.threads_title")}
          className="shrink-0 sm:hidden"
          size="icon"
          variant="outline"
          onClick={() => setThreadSheetOpen(true)}
        >
          <History aria-hidden="true" className="size-4" />
        </Button>
        <Button
          aria-label={t("agents.detail.new_thread")}
          className="h-9 shrink-0"
          onClick={handleNewThread}
        >
          <Plus aria-hidden="true" className="size-4" />
          <span className="hidden sm:inline">{t("agents.detail.new_thread")}</span>
        </Button>
      </header>

      <Sheet open={isThreadSheetOpen} onOpenChange={setThreadSheetOpen}>
        <SheetContent className="w-80 overflow-y-auto p-0 sm:hidden" side="left">
          <SheetHeader className="sr-only">
            <SheetTitle>{t("agents.detail.threads_title")}</SheetTitle>
          </SheetHeader>
          {/* Same sections, same order and same styles as the desktop rail —
              below `sm` the rail moves here instead of disappearing. */}
          <div className="flex flex-col gap-6 p-3">
            {/* The audience pill is NOT repeated here: it stays a single node
                in the header so `agent-audience` identifies exactly one
                element (B6 contract). The Microsoft 365 pill cannot follow it
                — the header has no room for it below `sm` — so this copy
                names itself instead: the header's `agent-m365-pill` stays the
                one node under that id at every breakpoint. */}
            <AgentM365Pill className="self-start sm:hidden" testId="agent-m365-pill-sheet" />
            <AgentThreadList
              activePresence={activePresence}
              activeSessionId={currentSessionId ?? undefined}
              agent={agent}
              isError={sessionsQuery.isError}
              isPending={sessionsQuery.isPending}
              sessions={sessionsQuery.data ?? []}
              onRetry={() => void sessionsQuery.refetch()}
              onSelect={handleSelectThread}
            />
            <AgentWorkingStateSection agentSlug={agent.slug} />
            <AgentArtifactsSection agentSlug={agent.slug} onSelect={handleOpenArtifact} />
            <AgentDeliveryReceipts agentId={agent.id} />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex min-h-0 flex-1">
        <aside className="bg-sidebar hidden w-72 shrink-0 flex-col gap-6 overflow-y-auto border-r p-3 sm:flex">
          <AgentThreadList
            activePresence={activePresence}
            activeSessionId={currentSessionId ?? undefined}
            agent={agent}
            isError={sessionsQuery.isError}
            isPending={sessionsQuery.isPending}
            sessions={sessionsQuery.data ?? []}
            onRetry={() => void sessionsQuery.refetch()}
            onSelect={handleSelectThread}
          />
          <AgentWorkingStateSection agentSlug={agent.slug} />
          <AgentArtifactsSection agentSlug={agent.slug} onSelect={handleOpenArtifact} />
          <AgentDeliveryReceipts agentId={agent.id} />
        </aside>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <AgentChatView
            agent={agent}
            session={currentSession}
            onPresenceChange={setActivePresence}
            onSessionEnsured={handleAdoptEnsuredSession}
          />
        </main>
      </div>

      <ArtifactViewerDialog
        agentSlug={agent.slug}
        artifact={viewedArtifact}
        open={isArtifactViewerOpen}
        onContinue={handleContinueArtifact}
        onOpenChange={setArtifactViewerOpen}
        onReuse={handleReuseArtifact}
      />
    </div>
  )
}
